import {
	Alert,
	AttributeEditor,
	AttributeEditorProps,
	Button,
	FormField,
	Header,
	Icon,
	Input,
	Select,
	Table,
} from '@amzn/awsui-components-console';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { OptionDefinition } from '@amzn/awsui-components-console/internal/components/option/interfaces';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { get } from 'lodash';

import { useGroupName, useIamRoles } from '../../data/redux';
import { DisplayMode } from 'src/components/helpers/content/contentInterfaces';
import { ReduxState } from '../../interfaces/reduxInterfaces';
import { HelpPanelInfoLink, LabeledContentSkeleton } from '../helpers';
import { useConditionalEffect } from '../../commons';
import { fetchIamRolesByGroup } from 'src/data/api/fetchFromAPI';
import { FROM_DIFF_STYLE, Rules, TO_DIFF_STYLE } from '../helpers/content/';
import Activity from 'src/interfaces/activityInterfaces';
import { SALTIRE } from 'src/constants/ariaLabels';
import { getContentDataAttributes } from 'src/commons/dataAttributes';
import PAGES from 'src/nav/Pages';
import InternalLink from '../helpers/InternalLink';
import { DataAccessIamRole } from '@amzn/aws-hammerstone-exposed-restful-service-typescript-client/clients/hammerstoneexposedrestfulservicelambda';

const label = 'IAM role(s)';
const IAM_ROLES_PATH = 'config.IAM_ROLES' as const;

interface IAMRoleObj {
	readonly type: string;
	readonly value: string;
}
const CLUSTER_ROLE_LABEL = 'Redshift cluster access role';
const CHAIN_ROLE_LABEL = 'Chain role';
const S3_ROLE_LABEL = 'S3 access role';

function iamRolesToLabeledItems(roles: { value: string }[]): IAMRoleObj[] {
	const length = roles?.length;
	if (!length) {
		return [{ type: S3_ROLE_LABEL, value: '' }];
	} else {
		return roles.map(({ value }, index) => {
			switch (index) {
				case length - 1:
					return { type: S3_ROLE_LABEL, value };
				case 0:
					return { type: CLUSTER_ROLE_LABEL, value };
				default:
					return { type: CHAIN_ROLE_LABEL, value };
			}
		});
	}
}

/**
 * Converts the IAM roles stored in the Redux store to options for the initial IAM Role `<Select>`
 *
 * Follows the filtering logic from the old Hammerstone site:  https://code.amazon.com/packages/AWSDWHammerstoneWebsite/blobs/6a73ed521ebcb835da97ffb1b4cd6a7ccea3b662/--/rails-root/app/controllers/manageactivity_controller.rb#L1833
 *
 * @param iamRolesObj An object mapping IAM arns to a DataAccessIamRole (returned from GetDataAccessIamRoles API)
 * @param ldapGroups The list of LDAP groups belonging to the user (to filter out the returned roles)
 * @returns Options for the IAM Role Select component
 */
function iamRolesByGroupToOptions(iamRoles: DataAccessIamRole[]): OptionDefinition[] {
	// Then combine/aggregate them into an object mapping IAM arn to an object with a list of descriptions and ldap groups
	const combinedUniqueIamRoleArns = (iamRoles ?? []).reduce((accumulator, currentValue) => {
		if (!(currentValue.iamRoleArn in accumulator)) {
			accumulator[currentValue.iamRoleArn] = { ...currentValue, descriptionSet: new Set(), ldapSet: new Set() };
		}
		// Add ldap group and description to lists
		accumulator[currentValue.iamRoleArn].ldapSet.add(currentValue.ldapGroup);
		accumulator[currentValue.iamRoleArn].descriptionSet.add(currentValue.description);
		return accumulator;
	}, {} as { [arn: string]: DataAccessIamRole & { descriptionSet: Set<string>; ldapSet: Set<string> } });

	// Sort combined roles by their arn string
	const sortedCombinedIamRoles = Object.values(combinedUniqueIamRoleArns).sort((iamRoleA, iamRoleB) =>
		iamRoleA.iamRoleArn.localeCompare(iamRoleB.iamRoleArn)
	);

	return sortedCombinedIamRoles.map(({ iamRoleArn, descriptionSet, ldapSet }) => ({
		label: iamRoleArn,
		value: iamRoleArn,
		// Join LDAP groups and description lists into a single string
		tags: ['LDAP:', ...Array.from(ldapSet).sort()],
		description: Array.from(descriptionSet).sort().join(' / '),
	}));
}

type S3AccessIAMRoleProps = {
	mode: DisplayMode;
	resourceId?: string;
	diffResourceId?: string;
	doesJobHaveOdin?: boolean;
	disabled?: boolean;
};

/**
 * A custom Content component specifically for managing IAM roles and chaining.
 */
export function S3AccessIAMRoleContent(props: Readonly<S3AccessIAMRoleProps>) {
	switch (props.mode) {
		case DisplayMode.Diff:
			return <DiffActivityIAMRoleContent {...props} />;
		case DisplayMode.Edit:
			// Edit returns an attribute editor which manages role definition and chaining
			return <EditIAMRoleContent {...props} />;
		case DisplayMode.View:
			//View returns a simple table with a useful `type` column next to every role
			return <ViewActivityIAMRoleContent {...props} />;
		case DisplayMode.Loading:
			return <LabeledContentSkeleton label={label} />;
		default:
			return null;
	}
}

const maxLengthRule = {
	value: 10,
	message:
		'No more than 10 IAM roles may be defined. At most you may have 1 S3 role, 8 chain roles, and 1 Redshift cluster role)',
} as const;

function EditIAMRoleContent(props: Readonly<S3AccessIAMRoleProps>) {
	const groupName = useGroupName();
	useConditionalEffect(() => {
		fetchIamRolesByGroup({ groupName });
	}, [groupName]);

	const { data: iamRolesByGroup, fetching } = useIamRoles(groupName);

	const iamRoleOptions = useMemo(() => iamRolesByGroupToOptions(iamRolesByGroup), [iamRolesByGroup]);
	const { control, formState } = useFormContext<Activity>();

	const { fields, update, append, remove } = useFieldArray({
		control,
		name: IAM_ROLES_PATH,
		rules: props?.doesJobHaveOdin
			? {
					required: false,
					maxLength: maxLengthRule,
			  }
			: {
					required: Rules.required(label),
					minLength: {
						value: 1,
						message: 'At least 1 role is required, please include an S3 IAM principal below.',
					},
					maxLength: maxLengthRule,
			  },
	});

	const definition: Readonly<AttributeEditorProps<IAMRoleObj>['definition']> = [
		{ label: 'Type', control: (item) => item.type },
		{
			label: 'Role',
			errorText: (_, index) => get(formState, `errors.${IAM_ROLES_PATH}[${index}].value.message`),
			control: (item, index) => {
				const { value, type } = item || {};
				const subpath = `${IAM_ROLES_PATH}.${index}.value` as const;
				// Reconstructing the props used for content data-* attributes
				const subprops = {
					mode: DisplayMode.Edit,
					path: subpath,
					label: 'S3 Role',
					resourceType: 'activity',
				} as const;

				return (
					<Controller
						control={control}
						name={subpath}
						rules={{
							required: {
								value: !props.disabled && !(props?.doesJobHaveOdin && index === 0),
								message:
									index === 0
										? `An S3 IAM role is required, please select one from the dropdown. `
										: `All included roles must be defined. To remove chain role, click the ${SALTIRE} button.`,
							},
							pattern: Rules.pattern.iamRoleArn('Role'),
						}}
						disabled={props.disabled}
						render={({ field, fieldState }) => {
							return index === 0 ? (
								<span {...getContentDataAttributes('Select', subprops)}>
									<Select
										disabled={props.disabled}
										selectedOption={value ? { value, label: value } : null}
										options={iamRoleOptions}
										filteringType="auto"
										ref={field.ref}
										onBlur={field.onBlur}
										invalid={fieldState.invalid}
										onChange={({ detail }) => {
											update(index, { value: detail.selectedOption.value });
											// Calling field.onChange will trigger an evaluation of whether this field violates its defined validation rules (local state)
											field.onChange(detail.selectedOption.value);
										}}
										placeholder={`Select ${type}...`}
									/>
								</span>
							) : (
								<span {...getContentDataAttributes('Text', subprops)}>
									<Input
										disabled={props.disabled}
										value={value}
										ref={field.ref}
										onBlur={field.onBlur}
										invalid={fieldState.invalid}
										onChange={({ detail }) => {
											update(index, { value: detail.value });
											// Calling field.onChange will trigger an evaluation of whether this field violates its defined validation rules (local state)
											field.onChange(detail.value);
										}}
										placeholder="e.g. arn:aws:iam::123456789012:role/role_name"
									/>
								</span>
							);
						}}
					/>
				);
			},
		},
	];
	return (
		<FormField
			// TODO: Eventually implement cooldown to prevent spamming of refresh button?
			label={
				<span>
					{label}
					<Button
						iconName="refresh"
						variant="inline-icon"
						data-testid="refreshIamRoles"
						loading={fetching}
						onClick={() => fetchIamRolesByGroup({ groupName }, true)}
					/>
				</span>
			}
			info={<HelpPanelInfoLink helpPanel="IamRoleChaining" />}
			errorText={get(formState, `errors.${IAM_ROLES_PATH}.root.message`)}
		>
			{props?.doesJobHaveOdin && !fields?.at(0)?.value && (
				<Alert type="warning" header="S3 Access Odin deprecation">
					This job is not configured with the appropriate permissions. Please select an IAM role below.
				</Alert>
			)}
			{get(formState.dirtyFields, IAM_ROLES_PATH)?.length > 0 && (
				<Alert type="warning">
					IAM role configuration changed. The roles are critical to Hammerstone permissions. Verify that your
					roles have the correct permissions before submitting. For more information, see the IAM role
					chaining info link above.
				</Alert>
			)}

			<span
				{...getContentDataAttributes('Text', {
					mode: DisplayMode.Edit,
					path: IAM_ROLES_PATH,
					label,
					resourceType: 'activity',
				})}
			>
				<AttributeEditor
					definition={definition}
					items={iamRolesToLabeledItems(fields)}
					onAddButtonClick={() => append({ value: '' })}
					onRemoveButtonClick={({ detail }) =>
						fields?.length > 1 ? remove(detail.itemIndex) : update(detail.itemIndex, { value: '' })
					}
					empty="No IAM roles defined"
					isItemRemovable={() => props?.doesJobHaveOdin || fields?.length > 1} // Do not remove the first role (IAM) unless it optional while migrating
					disableAddButton={props.disabled || fields?.length >= 10} // Do not add more than 10 roles total (1 IAM, 1 Redshift, 8 Chain)
					removeButtonText={SALTIRE}
					addButtonText={`+ Add ${fields?.length > 1 ? 'chain' : 'Redshift'} role`}
				/>
			</span>
		</FormField>
	);
}

const IAM_ROLE_COLUMN_DEFINITIONS = [
	{ id: 'roleType', header: 'Type', cell: (item: any) => item.type },
	{ id: 'roleValue', header: 'Role', cell: (item: any) => item.value },
];

function ViewActivityIAMRoleContent(props: Readonly<S3AccessIAMRoleProps>) {
	const roles = useSelector((state: ReduxState) => get(state.data.activity.data[props.resourceId], IAM_ROLES_PATH));

	if (!(roles?.length && roles[0]?.value?.length)) {
		return (
			<Alert type="warning" header="IAM Role(s) missing!">
				This activity is not configured with the appropriate permissions. Please{' '}
				<InternalLink groupName href={PAGES.EDIT_ACTIVITY.path} pathParams={{ activityId: props.resourceId }}>
					edit the activity
				</InternalLink>{' '}
				and add an IAM role for Redshift-S3 access.
			</Alert>
		);
	}

	return (
		<Table
			data-testid="IAMRoleContentTable"
			sortingDisabled
			header={
				<Header variant="h3" info={<HelpPanelInfoLink helpPanel="IamRoleChaining" />}>
					{label}
				</Header>
			}
			empty={'No IAM roles defined'}
			items={iamRolesToLabeledItems(roles)}
			columnDefinitions={IAM_ROLE_COLUMN_DEFINITIONS}
		/>
	);
}

function DiffActivityIAMRoleContent(props: Readonly<S3AccessIAMRoleProps>) {
	const fromRoles = useSelector(
		(state: ReduxState) => get(state.data.activity.data[props.resourceId], IAM_ROLES_PATH) ?? []
	);
	const toRoles = useSelector(
		(state: ReduxState) => get(state.data.activity.data[props.diffResourceId], IAM_ROLES_PATH) ?? []
	);
	const fromRolesList = fromRoles.map(({ value }) => value);
	const toRolesList = toRoles.map(({ value }) => value);

	const isEqual = fromRolesList.join(',') === toRolesList.join(',');
	const columnDefinitions = [...IAM_ROLE_COLUMN_DEFINITIONS];
	if (!isEqual) {
		// On diff, add an additional column indicating whether each role was added, changed, or removed
		columnDefinitions.unshift({
			id: 'diff',
			header: 'Diff',
			cell: ({ value }) => {
				const fromIndex = fromRolesList.indexOf(value);
				const toIndex = toRolesList.indexOf(value);

				if (fromIndex === -1) {
					// If the role does not exist in the "from" values list, it must have been added (+)
					return <Icon name="add-plus" variant="success" />;
				} else if (toIndex === -1) {
					// If the role does not exist in the "to" values list, it must have been removed (x)
					return <Icon name="close" variant="error" />;
				} else if (toIndex < fromIndex) {
					// The same role is found in an earlier position
					return <Icon name="angle-left" variant="warning" />;
				} else if (toIndex > fromIndex) {
					// The same role is found in a later position
					return <Icon name="angle-right" variant="warning" />;
				} else {
					// Indices are the same, implying the role's relative position is unchanged
					return '';
				}
			},
		});
	}

	return (
		<div>
			<div style={!isEqual ? FROM_DIFF_STYLE : {}}>
				<Table
					data-testid="IAMRoleContentTable-from-diff"
					sortingDisabled
					header={
						<Header variant="h3" info={<HelpPanelInfoLink helpPanel="IamRoleChaining" />}>
							{label}
						</Header>
					}
					empty={'No IAM roles defined'}
					items={iamRolesToLabeledItems(fromRoles)}
					columnDefinitions={columnDefinitions}
				/>
			</div>
			{!isEqual && <div> ↓ </div>}
			{!isEqual && (
				<div style={TO_DIFF_STYLE}>
					<Table
						data-testid="IAMRoleContentTable-to-diff"
						sortingDisabled
						header={
							<Header variant="h3" info={<HelpPanelInfoLink helpPanel="IamRoleChaining" />}>
								{label}
							</Header>
						}
						empty={'No IAM roles defined'}
						items={iamRolesToLabeledItems(toRoles)}
						columnDefinitions={columnDefinitions}
					/>
				</div>
			)}
		</div>
	);
}
